import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import DataTable from '../DataTable/DataTable';
import { fetchProducts, createProduct, editProduct, removeProduct } from '../../redux/services/productsService';
import { fetchCategories } from '../../redux/services/categoriesService';
import { fetchBrands } from '../../redux/services/brandsService';
import { fetchEvents } from '../../redux/services/eventService';
import { renderVariantLabel } from './VariantBuilder'; // ← make sure VariantBuilder.jsx is in the same folder
import VariantCard, { normalizeGstRate } from './VariantCard';
import { confirmDelete } from '../../utils/sweetalert';
import { hasPermission } from '../../utils/authHelper';
import API from '../../api/axiosInstance';
import AccessDenied from '../AccessDenied';
import { getImageUrl, toRelativePath } from '../../utils/imageUrl';

// Product Image Dimension Validator (1000x1000px 1:1 Square)
const PRODUCT_IMAGE_DIMENSIONS = {
  width: 1000,
  height: 1000,
  aspectRatio: 1,
  tolerance: 0.05,
  maxFileSize: 3 * 1024 * 1024,
  formats: [
    'image/jpeg', 'image/png', 'image/webp', 'image/gif',
    'image/svg+xml', 'image/bmp', 'image/tiff', 'image/x-icon',
    'image/heic', 'image/heif', 'image/avif'
  ],
};

const validateProductImageDimensions = (file) => {
  return new Promise((resolve) => {
    // Check file size
    if (file.size > PRODUCT_IMAGE_DIMENSIONS.maxFileSize) {
      resolve({
        valid: false,
        error: `File too large. Max: 3MB. You have: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
      });
      return;
    }

    // Check file format
    if (!PRODUCT_IMAGE_DIMENSIONS.formats.includes(file.type)) {
      resolve({
        valid: false,
        error: `Invalid format. Use common image formats (JPG, PNG, WebP, GIF, SVG, BMP, TIFF, ICO, HEIC, HEIF, AVIF). You have: ${file.type || 'unknown'}`,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const actualRatio = width / height;
        const expectedRatio = PRODUCT_IMAGE_DIMENSIONS.aspectRatio;
        const ratioDiff = Math.abs(actualRatio - expectedRatio) / expectedRatio;

        // Check aspect ratio (1:1 square) - commented out
        // if (ratioDiff > PRODUCT_IMAGE_DIMENSIONS.tolerance) {
        //   resolve({
        //     valid: false,
        //     error: `Incorrect aspect ratio. Use 1:1 square (${PRODUCT_IMAGE_DIMENSIONS.width}×${PRODUCT_IMAGE_DIMENSIONS.height}px). Yours: ${width}×${height}px`,
        //     dimensions: { width, height },
        //   });
        //   return;
        // }

        // Check max dimensions - commented out
        // if (width > 2500 || height > 2500) {
        //   resolve({
        //     valid: false,
        //     error: `Image dimensions too large. Max allowed is 2500x2500px. Yours: ${width}×${height}px`,
        //     dimensions: { width, height },
        //   });
        //   return;
        // }

        resolve({
          valid: true,
          dimensions: { width, height },
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const getVariantSignature = (attributes) => {
  if (!Array.isArray(attributes)) return '';
  return attributes
    .filter(a => a.key && a.value !== undefined && a.value !== null && String(a.value).trim() !== '')
    .map(a => `${a.key.trim().toLowerCase()}:${a.value.trim().toLowerCase()}`)
    .sort()
    .join(';');
};
const KM = {
  orange: 'var(--warning-600)', orangeLight: 'var(--warning-50)', blue: 'var(--primary-600)',
  green: 'var(--success-main)', teal: 'var(--info-500)', border: 'var(--border-color)',
  text: 'var(--text-primary-light)', muted: 'var(--neutral-500)', bg: 'var(--bg-color)',
};

// ── Styles ────────────────────────────────────────────────────────────────────
const formCard = { background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, marginBottom: 20 };
const formHeader = { background: KM.blue, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 10, borderTopLeftRadius: 11, borderTopRightRadius: 11 };
const headerIcon = { width: 32, height: 32, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 600 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 5 };
const labelStyle = { fontSize: 11, fontWeight: 500, color: KM.muted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { padding: '9px 12px', border: `1px solid ${KM.border}`, borderRadius: 8, fontSize: 13, color: KM.text, background: '#fff', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const tag = (color, bg) => ({ fontSize: 10, fontWeight: 700, color, background: bg, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' });
const errorStyle = { fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2 };

// stockColor is defined inside Products component using live inventory settings
function totalStock(p) { return p.Variants?.reduce((a, v) => a + Number(v.stock || 0), 0) ?? 0; }

// ── Blank variant (matches VariantBuilder's internal shape) ───────────────────
function blankVariantRow() {
  return {
    id: Date.now() + Math.random(),
    mrp: '',
    salesPrice: '',
    stock: '',
    status: 'Active',
    sku: '',
    attributes: [],
    gstMode: 'Inclusive',
    gstRate: '0%',
    imageFile: null,
    imagePreview: null,
    galleryFiles: [],
    galleryPreviews: []
  };
}

// ── Form defaults ─────────────────────────────────────────────────────────────
const BLANK_FORM = {
  productName: '', categoryId: '', categoryName: '', subCategoryId: '', subCategoryName: '',
  subSubCategoryId: '', subSubCategoryName: '',
  brandId: '', discount: '', shortDescription: '', fullDescription: '',
  tag: '', isNewArrival: false, isHotDeal: false, isRecommended: false, isFamilyPack: false,
  isPartialCodAvailable: true,
  customisationFields: {},
  shippingWeight: '', shippingLength: '', shippingBreadth: '', shippingHeight: '',
};



// ═══════════════════════════════════════════════════════════════════════════════
export default function Products({ showToast }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { items: allProducts, loading } = useSelector(s => s.products || {});
  const { items: rawCategories } = useSelector(s => s.categories || {});
  const { items: brands } = useSelector(s => s.brands || {});
  const { settings: invSettings } = useSelector(s => s.notifications);
  const stockColor = (q) => {
    const high = invSettings?.highStockThreshold ?? 51;
    const medium = invSettings?.mediumStockThreshold ?? 11;
    if (q === 0) return '#EF4444';
    if (q < medium) return '#EF4444';
    if (q < high) return '#F59E0B';
    return '#39B54A';
  };

  // Safe checks: ensure categories and brands are normalized arrays to prevent runtime crashes
  const categories = Array.isArray(rawCategories) ? rawCategories : [];
  const safeBrands = Array.isArray(brands) ? brands : [];

  // ── Core state ────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...BLANK_FORM });
  const [errors, setErrors] = useState({});
  const [variants, setVariants] = useState([blankVariantRow()]);
  const [variantTab, setVariantTab] = useState('options'); // 'options' | 'skus'

  // ── Multi-image state ─────────────────────────────────────────────────────
  const [imageFiles, setImageFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [imageDimensionsMap, setImageDimensionsMap] = useState({});
  const [originalPreviews, setOriginalPreviews] = useState([]); // snapshot on edit open
  const [customisationFieldsTemplates, setCustomisationFieldsTemplates] = useState([]);
  const fileInputRef = useRef();
  const variantBuilderRef = useRef();
  const [mainDragActive, setMainDragActive] = useState(false);

  const handleMainDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setMainDragActive(true);
    } else if (e.type === "dragleave") {
      setMainDragActive(false);
    }
  };

  const handleMainDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMainDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      handleFileChange({ target: { files } });
    }
  };



  // ── SubCategories derived from selected category safely ───────────────────
  const selectedCategory = categories.find(c => String(c.id) === String(formData.categoryId));
  const productCategories = categories.filter(c => c.value !== null && c.value !== undefined && c.value !== '');

  const subCategories = selectedCategory?.subcategories || selectedCategory?.subCategories || selectedCategory?.SubCategories || [];

  const selectedSubCategory = subCategories.find(s => String(s.id) === String(formData.subCategoryId));
  const subSubCategories = selectedSubCategory?.subsubcategories || selectedSubCategory?.subSubCategories || selectedSubCategory?.SubSubCategories || [];


  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchBrands());
    dispatch(fetchEvents());
    dispatch(fetchProducts());

  }, [location.search]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const isLowStock = new URLSearchParams(location.search).get('filter') === 'lowstock';
  const rows = isLowStock
    ? [...(allProducts || [])].filter(p => totalStock(p) < 50).sort((a, b) => totalStock(a) - totalStock(b))
    : (allProducts || []);

  if (!hasPermission('products_view')) {
    return <AccessDenied moduleName="Products" />;
  }

  // ── Image handlers ────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const currentLength = imageFiles.length;

    selected.forEach((file, idx) => {
      validateProductImageDimensions(file).then((result) => {
        setImageDimensionsMap(prev => ({
          ...prev,
          [currentLength + idx]: result,
        }));
      });
    });

    setImageFiles(prev => [...prev, ...selected]);
    setPreviews(prev => [...prev, ...selected.map(f => URL.createObjectURL(f))]);
    if (e.target && 'value' in e.target) {
      e.target.value = null;
    }
  };

  const removeImage = (index) => {
    if (previews[index]?.startsWith('blob:')) URL.revokeObjectURL(previews[index]);
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));

    // Rebuild imageDimensionsMap with updated indices
    setImageDimensionsMap(prev => {
      const newMap = {};
      Object.entries(prev).forEach(([idx, val]) => {
        const idxNum = parseInt(idx, 10);
        if (idxNum < index) {
          newMap[idxNum] = val;
        } else if (idxNum > index) {
          newMap[idxNum - 1] = val;
        }
      });
      return newMap;
    });
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const reset = () => {
    previews.forEach(url => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); });
    setEditingId(null);
    setShowForm(false);
    setFormData({ ...BLANK_FORM });
    setErrors({});
    setVariants([blankVariantRow()]);
    setVariantTab('options');
    setImageFiles([]);
    setPreviews([]);
    setImageDimensionsMap({});
    setOriginalPreviews([]);
  };

  // Cancel edit — restore original server images, discard local changes
  const handleCancel = () => {
    previews.forEach(url => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); });
    if (editingId) {
      // Restore snapshot taken when edit opened
      setPreviews([...originalPreviews]);
      setImageFiles([]);
      setImageDimensionsMap({});
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...BLANK_FORM });
      setErrors({});
      setVariants([blankVariantRow()]);
      setVariantTab('options');
      setOriginalPreviews([]);
    } else {
      reset();
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = (p) => {
    const catName = p.Category?.name || p.Category?.label || '';
    const subCatId = p.subCategoryId || p.SubCategory?.id || '';
    const subCatName = p.SubCategory?.name || p.SubCategory?.label || '';
    const subSubCatId = p.subSubCategoryId || p.SubSubCategory?.id || '';
    const subSubCatName = p.SubSubCategory?.name || p.SubSubCategory?.label || '';

    setEditingId(p.id);
    setFormData({
      productName: p.name || '',
      categoryId: p.categoryId || '',
      categoryName: catName,
      subCategoryId: subCatId,
      subCategoryName: subCatName,
      subSubCategoryId: subSubCatId,
      subSubCategoryName: subSubCatName,
      brandId: p.brandId || '',
      discount: p.discount ?? '',
      shortDescription: p.shortDescription || '',
      fullDescription: p.fullDescription || '',
      tag: Array.isArray(p.tag) ? p.tag.join(', ') : (p.tag || ''),

      isNewArrival: !!p.isNew,
      isHotDeal: !!p.isHotDeal,
      isRecommended: !!p.isRecommended,
      isFamilyPack: !!p.isFamilyPack,
      isPartialCodAvailable: p.isPartialCodAvailable !== false,
      customisationFields: (() => {
        let cf = p.customisationFields || {};
        if (typeof cf === 'string') {
          try { cf = JSON.parse(cf); } catch { cf = {}; }
        }
        const mapped = {};
        if (cf && typeof cf === 'object') {
          Object.keys(cf).forEach(k => {
            mapped[k] = !!cf[k];
          });
        }
        return mapped;
      })(),
      shippingWeight: p.shippingWeight || '',
      shippingLength: (() => {
        let sd = p.shippingDimensions;
        if (typeof sd === 'string') {
          try { sd = JSON.parse(sd); } catch { sd = null; }
        }
        return (sd && !Array.isArray(sd) && sd.length !== undefined && sd.length !== null) ? sd.length : '';
      })(),
      shippingBreadth: (() => {
        let sd = p.shippingDimensions;
        if (typeof sd === 'string') {
          try { sd = JSON.parse(sd); } catch { sd = null; }
        }
        return (sd && !Array.isArray(sd) && sd.breadth !== undefined && sd.breadth !== null) ? sd.breadth : '';
      })(),
      shippingHeight: (() => {
        let sd = p.shippingDimensions;
        if (typeof sd === 'string') {
          try { sd = JSON.parse(sd); } catch { sd = null; }
        }
        return (sd && !Array.isArray(sd) && sd.height !== undefined && sd.height !== null) ? sd.height : '';
      })(),
    });

    // ── Map existing variants → VariantBuilder shape ──────────────────────
    setVariants(
      p.Variants?.length
        ? p.Variants.map(v => {
          const parseVariantName = (name) => {
            if (!name || name === 'Default') return [];
            let customNoteCount = 0;
            return name.split(/\s*(?:·|\||,|\/|-)\s*/).map(part => {
              const ci = part.indexOf(':');
              if (ci === -1) {
                customNoteCount++;
                const key = customNoteCount === 1 ? 'Custom Note' : `Custom Note ${customNoteCount}`;
                return { key, value: part.trim(), customValue: '' };
              }
              return { key: part.slice(0, ci).trim(), value: part.slice(ci + 1).trim(), customValue: '' };
            }).filter(a => a.key && a.value);
          };

          const nameAttrs = parseVariantName(v.variantName);
          const rawAttrs = v.attributes;
          const parsedAttrs = typeof rawAttrs === 'string'
            ? (() => { try { return JSON.parse(rawAttrs); } catch { return []; } })()
            : (Array.isArray(rawAttrs) ? rawAttrs : []);

          // HEAL LOGIC: If parsed attributes from variantName do not match DB attributes,
          // automatically heal DB attributes to match the name-based ones.
          let finalAttrs = parsedAttrs;
          if (nameAttrs.length > 0) {
            const matches = nameAttrs.every(na =>
              parsedAttrs.some(pa =>
                na.key.toLowerCase() === pa.key.toLowerCase() &&
                na.value.toLowerCase() === pa.value.toLowerCase()
              )
            );
            if (!matches || parsedAttrs.length !== nameAttrs.length) {
              finalAttrs = nameAttrs;
            }
          }

          const builtAttrs = finalAttrs.length > 0
            ? finalAttrs.map(a => ({ key: a.key, value: a.value, customValue: '' }))
            : (() => {
              const fromFlat = [
                v.colour ? { key: 'Colour', value: v.colour, customValue: '' } : null,
                v.size ? { key: 'Size', value: v.size, customValue: '' } : null,
                v.material ? { key: 'Material', value: v.material, customValue: '' } : null,
                v.finish ? { key: 'Finish', value: v.finish, customValue: '' } : null,
                v.weight ? { key: 'Weight', value: v.weight, customValue: '' } : null,
                v.dimensions ? { key: 'Dimensions', value: v.dimensions, customValue: '' } : null,
                v.engraving ? { key: 'Engraving', value: v.engraving, customValue: '' } : null,
                v.printText ? { key: 'Print Text', value: v.printText, customValue: '' } : null,
                v.customLabel ? { key: 'Custom Note', value: v.customLabel, customValue: '' } : null,
              ].filter(Boolean);
              return fromFlat.length > 0 ? fromFlat : nameAttrs;
            })();

          const finalAttrsToSet = [...builtAttrs];

          return {
            id: v.id || Date.now() + Math.random(),
            open: false,
            mrp: v.mrp || '',
            salesPrice: v.salesPrice || '',
            stock: v.stock ?? '',
            status: v.status || 'Active',
            sku: v.sku || '',
            gstMode: v.gstMode || 'Inclusive',
            gstRate: normalizeGstRate(v.gstRate),
            imageFile: null,
            imagePreview: v.image ? getImageUrl(v.image) : null,
            galleryFiles: [],
            galleryPreviews: (() => {
              const rawImgs = v.images;
              let parsed = typeof rawImgs === 'string'
                ? (() => { try { return JSON.parse(rawImgs); } catch { return []; } })()
                : (Array.isArray(rawImgs) ? rawImgs : []);
              if ((!parsed || parsed.length === 0) && v.image) {
                parsed = [v.image];
              } else if (v.image && !parsed.some(img => img === v.image || getImageUrl(img) === getImageUrl(v.image))) {
                parsed = [v.image, ...parsed];
              }
              return (parsed || []).filter(Boolean).map(img => {
                const rawPath = typeof img === 'string' ? img : (img?.path || img?.url || '');
                return {
                  url: getImageUrl(rawPath),
                  path: toRelativePath(rawPath) || (typeof rawPath === 'string' ? rawPath : ''),
                  file: null
                };
              });
            })(),
            combo: finalAttrsToSet.map(a => ({ key: a.key, value: a.value })),
            variantName: (v.variantName
              ? v.variantName.split(/\s*(?:·|\||,|\/)\s*/).filter(part => {
                const ci = part.indexOf(':');
                if (ci !== -1) {
                  return part.slice(ci + 1).trim() !== '';
                }
                return part.trim() !== '';
              }).join(' · ')
              : null) || finalAttrsToSet.filter(a => a.key && a.key !== 'ColourHex' && a.value !== undefined && a.value !== null && String(a.value).trim() !== '').map(a => `${a.key}: ${a.value}`).join(' · ') || 'Default',
            attributes: finalAttrsToSet,
            shippingWeight: v.shippingWeight ? String(v.shippingWeight) : '',
            shippingDimensions: v.shippingDimensions || null,
          };
        })
        : [blankVariantRow()]
    );

    const imgs = p.images || (p.image ? (Array.isArray(p.image) ? p.image : [p.image]) : []);
    const mappedImgs = imgs.map(img => getImageUrl(img));
    setPreviews(mappedImgs);
    setOriginalPreviews(mappedImgs); // snapshot for cancel
    setImageFiles([]);
    setImageDimensionsMap({});
    setErrors({});
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getFirstInvalidImageError = () =>
    Object.values(imageDimensionsMap).find(result => result && !result.valid)?.error;

  const ErrorMsg = ({ field }) => {
    const message = field === 'images'
      ? (errors[field] || getFirstInvalidImageError())
      : errors[field];

    return message ? <span style={errorStyle}>⚠ {message}</span> : null;
  };

  const countWords = (text) => {
    return text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  };

  const DESC_LIMITS = {
    short: { maxWords: 30, maxChars: 200 },
    long: { minWords: 10, maxChars: 4000 },
  };

  const validateProduct = () => {
    const next = {};
    const discount = Number(formData.discount || 0);
    const variantErrors = [];

    if (!formData.productName.trim()) next.productName = 'Product Name is required';
    if (!formData.categoryId) next.categoryId = 'Please select a category';
    if (Number.isNaN(discount) || discount < 0 || discount > 100) next.discount = 'Discount must be between 0 and 100';
    if (!variants.length) next.variants = 'Please add at least one variant';

    // Validate main product image is mandatory
    if (!previews.length && !imageFiles.length) {
      next.images = 'Main default product image is mandatory';
    }

    // Check if there is any invalid image dimensions in map - commented out
    // const invalidImageError = getFirstInvalidImageError();
    // if (invalidImageError) {
    //   next.images = invalidImageError;
    // }

    // Description word count validation
    const shortWordCount = countWords(formData.shortDescription);
    if (formData.shortDescription && shortWordCount > DESC_LIMITS.short.maxWords) {
      next.shortDescription = `Short description must be maximum ${DESC_LIMITS.short.maxWords} words (currently ${shortWordCount}).`;
    }

    const longWordCount = countWords(formData.fullDescription);
    if (formData.fullDescription && longWordCount < DESC_LIMITS.long.minWords) {
      next.fullDescription = `Long description must be at least ${DESC_LIMITS.long.minWords} words (currently ${longWordCount}).`;
    }

    // Shipping dimensions & weight validation (all mandatory)
    const sWeight = parseFloat(formData.shippingWeight);
    if (formData.shippingWeight === '' || formData.shippingWeight === undefined || formData.shippingWeight === null || isNaN(sWeight) || sWeight <= 0) {
      next.shippingWeight = 'Weight is required and must be greater than 0';
    }

    const sLength = parseFloat(formData.shippingLength);
    if (formData.shippingLength === '' || formData.shippingLength === undefined || formData.shippingLength === null || isNaN(sLength) || sLength <= 0) {
      next.shippingLength = 'Length is required and must be greater than 0';
    }

    const sBreadth = parseFloat(formData.shippingBreadth);
    if (formData.shippingBreadth === '' || formData.shippingBreadth === undefined || formData.shippingBreadth === null || isNaN(sBreadth) || sBreadth <= 0) {
      next.shippingBreadth = 'Breadth is required and must be greater than 0';
    }

    const sHeight = parseFloat(formData.shippingHeight);
    if (formData.shippingHeight === '' || formData.shippingHeight === undefined || formData.shippingHeight === null || isNaN(sHeight) || sHeight <= 0) {
      next.shippingHeight = 'Height is required and must be greater than 0';
    }

    // Check duplicates inside the variant table
    const signatures = (variants || []).map(v => getVariantSignature(v.attributes));
    const duplicateSignatures = signatures.filter((sig, idx) => sig && signatures.indexOf(sig) !== idx);

    const skusInput = (variants || []).map(v => String(v.sku || '').trim().toLowerCase()).filter(Boolean);
    const duplicateSkus = skusInput.filter((sku, idx) => skusInput.indexOf(sku) !== idx);

    // Get list of existing SKUs on other products
    const existingGlobalSkus = [];
    (allProducts || []).forEach(p => {
      if (editingId && String(p.id) === String(editingId)) return;
      if (Array.isArray(p.variation)) {
        p.variation.forEach(v => {
          if (v.sku) existingGlobalSkus.push(String(v.sku).trim().toLowerCase());
        });
      }
    });

    variants.forEach((v, index) => {
      const messages = [];
      const mrp = Number(v.mrp);
      const salesPrice = Number(v.salesPrice);
      const stock = Number(v.stock);

      const activeAttrs = (v.attributes || []).filter(a => a.key && a.key !== 'ColourHex');
      const emptyAttrs = activeAttrs.filter(a => a.value === undefined || a.value === null || String(a.value).trim() === '');
      const activeKeys = activeAttrs.map(a => a.key);

      const filledAttrs = activeAttrs.filter(a => a.value && String(a.value).trim() !== '');
      if (filledAttrs.length === 0) {
        messages.push('Please provide at least one option (e.g. Weight or Volume) for this variant');
      }

      // const sig = getVariantSignature(v.attributes);
      // if (sig && duplicateSignatures.includes(sig)) {
      //   messages.push('Duplicate variant combination in table');
      // }

      const skuVal = String(v.sku || '').trim().toLowerCase();
      if (skuVal && duplicateSkus.includes(skuVal)) {
        messages.push('Duplicate SKU code in table');
      }
      if (skuVal && existingGlobalSkus.includes(skuVal)) {
        messages.push('SKU code is already used by another product');
      }

      if (!v.mrp) messages.push('Enter MRP');
      else if (Number.isNaN(mrp) || mrp <= 0) messages.push('MRP must be greater than 0');
      if (!v.salesPrice) messages.push('Enter sales price');
      else if (Number.isNaN(salesPrice) || salesPrice <= 0) messages.push('Sales price must be greater than 0');
      else if (!Number.isNaN(mrp) && mrp > 0 && salesPrice > mrp) messages.push('Sales price cannot be greater than MRP');
      if (v.stock === '') messages.push('Enter stock');
      else if (Number.isNaN(stock) || stock < 0) messages.push('Stock cannot be negative');

      if (!v.galleryPreviews || v.galleryPreviews.length === 0) {
        messages.push('Upload at least one image in the variant gallery');
      }

      // const firstGalleryError = Object.values(v.galleryErrors || {}).find(Boolean);
      // if (firstGalleryError) {
      //   messages.push(firstGalleryError);
      // }

      variantErrors[index] = messages;
    });

    if (variantErrors.some(list => list.length)) {
      next.variantErrors = variantErrors;
      setVariantTab('skus');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateProduct()) {
      showToast.error('Please fill in all mandatory fields before submitting');
      return;
    }
    const fd = new FormData();
    fd.append('productName', formData.productName);
    fd.append('categoryId', formData.categoryId);
    fd.append('brandId', formData.brandId);
    fd.append('discount', formData.discount || 0);
    fd.append('shortDescription', formData.shortDescription);
    fd.append('fullDescription', formData.fullDescription);
    fd.append('isNewArrival', formData.isNewArrival);

    fd.append('isHotDeal', formData.isHotDeal);
    fd.append('isRecommended', formData.isRecommended);
    fd.append('isFamilyPack', formData.isFamilyPack);
    fd.append('isPartialCodAvailable', formData.isPartialCodAvailable);
    fd.append('shippingWeight', formData.shippingWeight || '');
    fd.append('shippingDimensions', JSON.stringify({
      length: formData.shippingLength ? parseFloat(formData.shippingLength) : null,
      breadth: formData.shippingBreadth ? parseFloat(formData.shippingBreadth) : null,
      height: formData.shippingHeight ? parseFloat(formData.shippingHeight) : null
    }));

    if (formData.subCategoryId) fd.append('subCategoryId', formData.subCategoryId);
    if (formData.subCategoryName) fd.append('subCategoryName', formData.subCategoryName);
    if (formData.subSubCategoryId) fd.append('subSubCategoryId', formData.subSubCategoryId);
    if (formData.subSubCategoryName) fd.append('subSubCategoryName', formData.subSubCategoryName);

    const baseTags = formData.tag ? formData.tag.split(',').map(t => t.trim()).filter(Boolean) : [];

    if (formData.isNewArrival && !baseTags.includes('new-arrival')) baseTags.push('new-arrival');
    if (formData.isHotDeal && !baseTags.includes('hot-deal')) baseTags.push('hot-deal');
    fd.append('tag', JSON.stringify(baseTags));

    // ── Map Variant state → API payload ────────────────────────────
    const mappedVariants = variants.map((v, idx) => {
      const attributes = (v.attributes || [])
        .filter(a => a.key && a.value && String(a.value).trim() !== '')
        .map(a => {
          let newKey = a.key;
          const valLower = String(a.value).trim().toLowerCase();
          if (newKey.toLowerCase().includes('custom')) {
            if (/^\d+\.?\d*\s*(ml|l|ltr)$/.test(valLower)) {
              newKey = 'Volume';
            } else if (/^\d+\.?\d*\s*(g|kg)$/.test(valLower)) {
              newKey = 'Weight';
            }
          }
          return { key: newKey, value: a.value };
        });

      const findAttr = (key) => {
        const found = attributes.find(a => a.key === key);
        return found ? found.value : '';
      };

      const gstRateVal = parseFloat(String(v.gstRate || 0).replace('%', '')) || 0;

      return {
        id: v.id && !String(v.id).startsWith('new_') ? v.id : undefined,
        variantName: v.variantName || 'Default',
        attributes,
        mrp: v.mrp,
        salesPrice: v.salesPrice,
        stock: v.stock || 0,
        sku: v.sku || null,
        gstMode: v.gstMode || 'Inclusive',
        gstRate: gstRateVal,
        unit: findAttr('Size') || 'Free Size',
        colour: findAttr('Colour'),
        size: findAttr('Size'),
        image: (!v.imageFile && v.imagePreview && !v.imagePreview.startsWith('blob:'))
          ? toRelativePath(v.imagePreview)
          : (v.galleryPreviews?.[0] ? (v.galleryPreviews[0].path || toRelativePath(v.galleryPreviews[0].url)) : undefined),
        images: v.galleryPreviews
          ? v.galleryPreviews.filter(item => item && !item.file).map(item => item.path || toRelativePath(item.url)).filter(Boolean)
          : [],
        status: v.status || 'Active',
        shippingWeight: formData.shippingWeight !== '' && formData.shippingWeight !== undefined
          ? formData.shippingWeight
          : (v.shippingWeight || undefined),
        shippingDimensions: {
          length: formData.shippingLength ? parseFloat(formData.shippingLength) : (v.shippingDimensions?.length ? parseFloat(v.shippingDimensions.length) : null),
          breadth: formData.shippingBreadth ? parseFloat(formData.shippingBreadth) : (v.shippingDimensions?.breadth ? parseFloat(v.shippingDimensions.breadth) : null),
          height: formData.shippingHeight ? parseFloat(formData.shippingHeight) : (v.shippingDimensions?.height ? parseFloat(v.shippingDimensions.height) : null)
        },
      };
    });

    fd.append('variants', JSON.stringify(mappedVariants));

    variants.forEach((v, idx) => {
      const filesToUpload = v.galleryPreviews
        ? v.galleryPreviews.filter(item => item && item.file).map(item => item.file)
        : [];
      filesToUpload.forEach(file => {
        fd.append(`variantGallery_${idx}`, file);
      });
    });

    imageFiles.forEach(file => fd.append('images', file));
    const savedImages = previews
      .filter(url => url && !url.startsWith('blob:'))
      .map(url => toRelativePath(url))
      .filter(Boolean);
    fd.append('existingImages', JSON.stringify(savedImages));

    const tid = showToast.loading(editingId ? 'Updating…' : 'Adding…');
    try {
      if (editingId) {
        await dispatch(editProduct({ id: editingId, formData: fd }));
        showToast.success('Updated!', tid);
      } else {
        await dispatch(createProduct(fd));
        showToast.success('Added!', tid);
      }
      reset();
      dispatch(fetchProducts());
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Operation failed', tid);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    confirmDelete({
      title: 'Delete Product?',
      message: 'Are you sure you want to delete this product?',
      onConfirm: async () => {
        const tid = showToast.loading('Deleting product...');
        try {
          await dispatch(removeProduct(id));
          showToast.success('Product deleted successfully!', tid);
        } catch (err) {
          console.error('Failed to delete product:', err);
          const errMsg = err?.response?.data?.message || err?.message || 'Failed to delete product';
          showToast.error(errMsg, tid);
        }
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="section-title">Products Management</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isLowStock && (
            <button className="action-btn" onClick={() => window.location.href = '/products'}
              style={{ background: '#eee', color: '#333' }}>Clear Filter</button>
          )}
          {showForm ? (
            <button className="action-btn btn-edit" onClick={handleCancel}>
              Close
            </button>
          ) : (
            hasPermission('products_create') && (
              <button className="action-btn btn-edit" onClick={() => setShowForm(true)}>
                + Add Product
              </button>
            )
          )}
        </div>
      </div>

      {showForm && (
        <div style={formCard}>
          <div style={formHeader}>
            <div style={headerIcon}>🎁</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>
                {editingId ? 'Edit Product' : 'Add New Product'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
                Healthy Homes — natural product details &amp; variants
              </div>
            </div>
          </div>

          <div style={{ padding: '20px 24px 24px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Product Name *</label>
                <input style={inputStyle} required value={formData.productName}
                  onChange={e => setFormData({ ...formData, productName: e.target.value })}
                  placeholder="e.g. Wood Pressed Coconut Oil 1L, Himalayan Pink Salt" />
                <ErrorMsg field="productName" />
              </div>

              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Category *</label>
                <select style={inputStyle} value={formData.categoryId}
                  onChange={e => {
                    const cat = productCategories.find(c => String(c.id) === String(e.target.value));
                    setFormData({
                      ...formData,
                      categoryId: e.target.value,
                      categoryName: cat?.name || cat?.label || '',
                      subCategoryId: '',
                      subCategoryName: '',
                      subSubCategoryId: '',
                      subSubCategoryName: '',
                    });
                  }}>
                  <option value="">Select Category</option>
                  {productCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name || c.label}</option>
                  ))}
                </select>
                <ErrorMsg field="categoryId" />
              </div>

              {/* ── Short Description ── */}
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Short Description <span style={{ color: KM.muted, textTransform: 'none', fontWeight: 400, fontSize: 10 }}>(max {DESC_LIMITS.short.maxWords} words)</span></span>
                  <span style={{ fontSize: 11, color: (() => { const w = countWords(formData.shortDescription); return (w > DESC_LIMITS.short.maxWords) ? '#ef4444' : '#9ca3af'; })(), fontWeight: 500, textTransform: 'none' }}>
                    {formData.shortDescription.length}/{DESC_LIMITS.short.maxChars} chars · {countWords(formData.shortDescription)}/{DESC_LIMITS.short.maxWords} words
                  </span>
                </label>
                <input
                  style={{
                    ...inputStyle,
                    borderColor: errors.shortDescription ? '#ef4444' : undefined,
                    boxShadow: errors.shortDescription ? '0 0 0 2px rgba(239,68,68,0.1)' : undefined,
                  }}
                  value={formData.shortDescription}
                  onChange={e => {
                    const val = e.target.value;
                    setFormData({ ...formData, shortDescription: val });
                    const wc = countWords(val);
                    if (val && wc > DESC_LIMITS.short.maxWords) {
                      setErrors(prev => ({ ...prev, shortDescription: `Short description must be maximum ${DESC_LIMITS.short.maxWords} words (currently ${wc})` }));
                    } else {
                      setErrors(prev => { const n = { ...prev }; delete n.shortDescription; return n; });
                    }
                  }}
                  placeholder="e.g. 100% pure wood pressed oil extracted through traditional wooden ghani methods"
                  maxLength={DESC_LIMITS.short.maxChars}
                />
                <ErrorMsg field="shortDescription" />
              </div>

              {/* ── Full Description ── */}
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Full Description <span style={{ color: KM.muted, textTransform: 'none', fontWeight: 400, fontSize: 10 }}>(min {DESC_LIMITS.long.minWords} words)</span></span>
                  <span style={{ fontSize: 11, color: (() => { const w = countWords(formData.fullDescription); return (w > 0 && w < DESC_LIMITS.long.minWords) ? '#ef4444' : '#9ca3af'; })(), fontWeight: 500, textTransform: 'none' }}>
                    {formData.fullDescription.length}/{DESC_LIMITS.long.maxChars} chars · {countWords(formData.fullDescription)}/{DESC_LIMITS.long.minWords}+ words
                  </span>
                </label>
                <textarea
                  style={{
                    ...inputStyle,
                    minHeight: 80,
                    resize: 'vertical',
                    borderColor: errors.fullDescription ? '#ef4444' : undefined,
                    boxShadow: errors.fullDescription ? '0 0 0 2px rgba(239,68,68,0.1)' : undefined,
                  }}
                  value={formData.fullDescription}
                  onChange={e => {
                    const val = e.target.value;
                    setFormData({ ...formData, fullDescription: val });
                    const wc = countWords(val);
                    if (val && wc < DESC_LIMITS.long.minWords) {
                      setErrors(prev => ({ ...prev, fullDescription: `Long description must be at least ${DESC_LIMITS.long.minWords} words (currently ${wc})` }));
                    } else {
                      setErrors(prev => { const n = { ...prev }; delete n.fullDescription; return n; });
                    }
                  }}
                  placeholder="Ingredients, extraction method, nutritional benefits, storage instructions, packaging details…"
                  maxLength={DESC_LIMITS.long.maxChars}
                />
                <ErrorMsg field="fullDescription" />
              </div>

              {/* Media Zone for Default Product Image */}
              <div style={{ ...formCard, gridColumn: 'span 2', marginBottom: 0 }}>
                <div style={{ ...formHeader, background: KM.blue }}>
                  <div style={headerIcon}>🖼️</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Media</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>Main default product image</div>
                  </div>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {previews.length > 0 ? (
                    <div style={{ position: 'relative', width: 220, height: 260 }}>
                      <img src={previews[0]} alt="Main product default" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10, border: `2px solid ${KM.border}` }} />
                      <button type="button" onClick={() => removeImage(0)} style={{ position: 'absolute', top: -10, right: -10, width: 24, height: 24, borderRadius: '50%', background: KM.orange, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current.click()}
                      onDragEnter={handleMainDrag}
                      onDragLeave={handleMainDrag}
                      onDragOver={handleMainDrag}
                      onDrop={handleMainDrop}
                      style={{
                        width: '100%',
                        height: 160,
                        border: `2px dashed ${mainDragActive ? '#00b4d8' : KM.border}`,
                        borderRadius: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        background: mainDragActive ? 'rgba(0,180,216,0.08)' : '#fafafa',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <span style={{ fontSize: 32 }}>📤</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: mainDragActive ? '#00b4d8' : KM.blue, marginTop: 8 }}>Click to Upload Main Image</span>
                      <span style={{ fontSize: 11, color: KM.muted, marginTop: 4 }}>1:1 Square (1000×1000px) • Max: 3MB</span>
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <ErrorMsg field="images" />
                  </div>
                </div>
              </div>

              {variants.map((variant, index) => [
                <VariantCard
                  key={`card-${variant.id || index}`}
                  variant={variant}
                  index={index}
                  isDynamicBuilder={true}
                  showToast={showToast}
                  onCopyPrevious={variants.length > 0 && index > 0 ? () => {
                    // Copy existing variant's attributes from the precise previous variant (index - 1)
                    // We copy the schema (the keys and types) but empty the values so they can input fresh weight/volume
                    const prevVariant = variants[index - 1];
                    if (!prevVariant || !prevVariant.attributes) return;
                    const clonedSchema = prevVariant.attributes.map(a => ({
                      key: a.key,
                      value: a.value // Keep the original value so they can clearly see and reuse what they gave earlier
                    }));

                    const updatedVariant = { ...variant, attributes: clonedSchema };
                    const uv = [...variants];
                    uv[index] = updatedVariant;
                    setVariants(uv);
                  } : undefined}
                  onRemove={variants.length > 1 ? () => {
                    const newVariants = [...variants];
                    newVariants.splice(index, 1);
                    setVariants(newVariants);
                  } : undefined}
                  onChange={(updatedVariant, i) => {
                    const uv = [...variants];
                    uv[i === undefined ? index : i] = updatedVariant;
                    setVariants(uv);
                    if (index === 0 && updatedVariant.mrp && updatedVariant.salesPrice && Number(updatedVariant.mrp) > 0) {
                      const auto = Math.round((1 - Number(updatedVariant.salesPrice) / Number(updatedVariant.mrp)) * 100);
                      if (auto >= 0 && auto <= 100) setFormData(f => ({ ...f, discount: String(auto) }));
                    }
                  }}
                  errors={errors.variantErrors?.[index]}
                  title={`Variant ${index + 1}`}
                  subtitle="Fill in the variant details below"
                />,
                <div key={`err-${variant.id || index}`} style={{ gridColumn: 'span 2', marginTop: -10 }}>
                  {errors.variantErrors && errors.variantErrors[index] && errors.variantErrors[index].length > 0 && (
                    <span style={errorStyle}>{errors.variantErrors[index].join(' · ')}</span>
                  )}
                </div>
              ])}

              {/* <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setVariants([...variants, blankVariantRow()])}
                  style={{
                    padding: '8px 24px',
                    background: '#e0f2fe',
                    color: KM.blue,
                    border: `1.5px dashed ${KM.blue}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <span style={{ fontSize: 16 }}>+</span> Add Another Variant
                </button>
              </div> */}



              {/* ── Product Placement & Showcase Sections ── */}
              <div style={{
                gridColumn: 'span 2',
                padding: '16px 20px',
                background: '#F8FAFC',
                border: `1px solid ${KM.border}`,
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B', marginBottom: 4 }}>
                  📌 Homepage &amp; Showcase Sections
                </div>
                <div style={{ fontSize: 11, color: '#64748B', marginBottom: 12 }}>
                  Select the storefront collections and carousel sections where this product should be featured
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { key: 'isNewArrival', label: '✨ New Arrivals' },
                    { key: 'isHotDeal', label: '🛒 Shop Your Cart' },
                    { key: 'isRecommended', label: '👍 Recommended For You' },
                  ].map(item => (
                    <label key={item.key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: formData[item.key] ? '#0F172A' : '#475569',
                      background: formData[item.key] ? '#E0F2FE' : '#FFFFFF',
                      border: `1.5px solid ${formData[item.key] ? '#0284C7' : '#E2E8F0'}`,
                      padding: '8px 14px',
                      borderRadius: 8,
                      transition: 'all 0.15s ease',
                      userSelect: 'none'
                    }}>
                      <input
                        type="checkbox"
                        checked={!!formData[item.key]}
                        onChange={e => setFormData({ ...formData, [item.key]: e.target.checked })}
                        style={{ width: 16, height: 16, accentColor: '#0284C7', cursor: 'pointer' }}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* ── Shiprocket Logistics & Dimensions Card ── */}
              <div style={{ ...formCard, gridColumn: 'span 2', marginBottom: 0, border: '1px solid #E0E7FF' }}>
                <div style={{ ...formHeader, background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}>
                  <div style={headerIcon}>📦</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Shiprocket Logistics & Package Dimensions</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>Weight & box dimensions used for live courier rates and automatic shipping generation</div>
                  </div>
                </div>
                <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Weight (kg) <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      style={{ ...inputStyle, ...(errors.shippingWeight ? { borderColor: '#ef4444', background: '#FEF2F2' } : {}) }}
                      value={formData.shippingWeight}
                      onChange={e => {
                        setFormData({ ...formData, shippingWeight: e.target.value });
                        if (errors.shippingWeight) setErrors(prev => ({ ...prev, shippingWeight: null }));
                      }}
                      placeholder="e.g. 0.5"
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Package dead weight (in kg)</span>
                    <ErrorMsg field="shippingWeight" />
                  </div>

                  <div style={fieldStyle}>
                    <label style={labelStyle}>Length (cm) <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.5"
                      required
                      style={{ ...inputStyle, ...(errors.shippingLength ? { borderColor: '#ef4444', background: '#FEF2F2' } : {}) }}
                      value={formData.shippingLength}
                      onChange={e => {
                        setFormData({ ...formData, shippingLength: e.target.value });
                        if (errors.shippingLength) setErrors(prev => ({ ...prev, shippingLength: null }));
                      }}
                      placeholder="e.g. 15"
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Box length (in cm)</span>
                    <ErrorMsg field="shippingLength" />
                  </div>

                  <div style={fieldStyle}>
                    <label style={labelStyle}>Breadth (cm) <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.5"
                      required
                      style={{ ...inputStyle, ...(errors.shippingBreadth ? { borderColor: '#ef4444', background: '#FEF2F2' } : {}) }}
                      value={formData.shippingBreadth}
                      onChange={e => {
                        setFormData({ ...formData, shippingBreadth: e.target.value });
                        if (errors.shippingBreadth) setErrors(prev => ({ ...prev, shippingBreadth: null }));
                      }}
                      placeholder="e.g. 10"
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Box width / breadth (in cm)</span>
                    <ErrorMsg field="shippingBreadth" />
                  </div>

                  <div style={fieldStyle}>
                    <label style={labelStyle}>Height (cm) <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.5"
                      required
                      style={{ ...inputStyle, ...(errors.shippingHeight ? { borderColor: '#ef4444', background: '#FEF2F2' } : {}) }}
                      value={formData.shippingHeight}
                      onChange={e => {
                        setFormData({ ...formData, shippingHeight: e.target.value });
                        if (errors.shippingHeight) setErrors(prev => ({ ...prev, shippingHeight: null }));
                      }}
                      placeholder="e.g. 8"
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Box height (in cm)</span>
                    <ErrorMsg field="shippingHeight" />
                  </div>
                </div>
              </div>

              {/* Customisation Fields — visible only when Customisable is checked 
              {formData.isCustomisable && (
                <div style={{ gridColumn: 'span 2', padding: '12px 14px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Customisation Input Fields</div>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {customisationFieldsTemplates.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#166534', opacity: 0.8 }}>No active customisation fields found. Add them in Settings → Customisation Fields first.</div>
                    ) : (
                      customisationFieldsTemplates.map(field => (
                        <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500, color: '#166534' }}>
                          <input type="checkbox"
                            checked={!!(formData.customisationFields?.[field.key])}
                            onChange={e => setFormData({
                              ...formData,
                              customisationFields: { ...formData.customisationFields, [field.key]: e.target.checked }
                            })} />
                          {field.icon ? <span style={{ marginRight: 2 }}>{field.icon}</span> : null}
                          {field.label} {field.isRequired ? <span style={{ color: '#ef4444' }}>*</span> : null}
                        </label>
                      ))
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#166534', marginTop: 8 }}>Selected fields will appear as input fields on the product page for customers to fill in.</div>
                </div>
              )}
              */}

              {/* Hidden file input - product images handled per-variant */}
              <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/x-icon,image/heic,image/heif,image/avif"
                style={{ display: 'none' }} onChange={handleFileChange} />

              <div className="km-form-actions km-field-full" style={{ display: "flex", gap: "10px" }}>
                <button type="submit" className="km-btn-submit">
                  {editingId ? 'Update Product' : 'Save Product'}
                </button>
                {editingId && (
                  <button type="button" className="km-btn-cancel" onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <p style={{ padding: 20 }}>Loading Products...</p> : (
        <DataTable
          columns={(() => {
            const cols = ['No.', 'Gallery', 'Product', 'Category', 'Price', 'Disc', 'Stock', 'Variants', 'Sections'];
            if (hasPermission('products_edit') || hasPermission('products_delete')) cols.push('Actions');
            return cols;
          })()}
          initialRows={rows}
          renderRow={(row, i) => {
            const firstVar = row.Variants?.[0];
            const stock = totalStock(row);
            const productImgs = row.images || (row.image ? (Array.isArray(row.image) ? row.image : [row.image]) : []);
            const variantImgs = (row.Variants || []).map(v => v.image).filter(Boolean);
            const imgs = productImgs.length ? productImgs : variantImgs;
            return (
              <tr key={row.id}>
                <td style={{ color: KM.muted, fontSize: 11 }}>{i + 1}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {imgs.slice(0, 3).map((img, idx) => (
                      <img key={idx} src={getImageUrl(img)} alt=""
                        width="36" height="36"
                        style={{ borderRadius: 7, objectFit: 'cover', border: `1px solid ${KM.border}` }} />
                    ))}
                    {imgs.length > 3 && (
                      <div style={{
                        width: 36, height: 36, borderRadius: 7,
                        background: KM.bg, border: `1px solid ${KM.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: KM.muted, fontWeight: 600,
                      }}>+{imgs.length - 3}</div>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{row.name}</div>
                  {row.shortDescription && (
                    <div style={{ fontSize: 11, color: KM.muted, marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.shortDescription}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: 12, fontWeight: 500 }}>
                  {row.Category?.name || row.Category?.label || '—'}
                </td>

                <td style={{ fontWeight: 700 }}>₹{firstVar?.salesPrice ?? row.price ?? 0}</td>
                <td>
                  {row.discount > 0
                    ? <span style={tag(KM.orange, KM.orangeLight)}>{row.discount}% off</span>
                    : <span style={{ color: KM.muted }}>—</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: stockColor(stock) }} />
                    <span style={{ fontWeight: 700, color: stockColor(stock) }}>{stock}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 400, maxHeight: 120, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
                    {row.Variants?.slice(0, 5).map((v, j) => {
                      const label = v.variantName || '—';
                      return (
                        <span key={j} title={label} style={{
                          fontSize: 10,
                          padding: '3px 7px',
                          borderRadius: 4,
                          background: '#f0f4ff',
                          color: KM.blue,
                          border: `1px solid #c7d4f0`,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}>
                          {renderVariantLabel(label, 10, 3)}
                        </span>
                      );
                    })}
                    {row.Variants?.length > 5 && (
                      <span style={{
                        fontSize: 10,
                        padding: '3px 7px',
                        borderRadius: 4,
                        background: '#f3f4f6',
                        color: KM.muted,
                        border: `1px solid #e5e7eb`,
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}>
                        +{row.Variants.length - 5} more
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {row.isNew && <span style={tag(KM.green, '#ecfdf5')}>NEW ARRIVAL</span>}
                    {row.isRecommended && <span style={tag('#8b5cf6', '#ede9fe')}>RECOMMENDED</span>}
                    {row.isHotDeal && <span style={tag(KM.orange, KM.orangeLight)}>SHOP YOUR CART</span>}
                    {!row.isNew && !row.isRecommended && !row.isHotDeal && (
                      <span style={{ color: KM.muted, fontSize: 11 }}>—</span>
                    )}
                  </div>
                </td>
                {(hasPermission('products_edit') || hasPermission('products_delete')) && (
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {hasPermission('products_edit') && (
                        <button className="action-btn btn-edit" onClick={() => handleEdit(row)}>Edit</button>
                      )}
                      {hasPermission('products_delete') && (
                        <button className="action-btn btn-del" onClick={() => handleDelete(row.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          }}
        />
      )}
    </div>
  );
}